import React, { useState, useEffect, useCallback } from "react";
import {
  FileText,
  Sparkles,
  Edit2
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { produtoDadosService } from "../../services/produtoDadosService";
import FichaTecnicaModal from "../modals/FichaTecnicaModal";
import GeradorAnuncioIAModal from "./GeradorAnuncioIAModal";

/**
 * SecaoBomETecnica.js — Seção de Ficha Técnica e Gerador de Anúncio IA
 */
export default function SecaoBomETecnica({ skuPai, item, produtoDados: externalProdutoDados, onProdutoDadosUpdated }) {
  const [produtoDadosRecord, setProdutoDadosRecord] = useState(externalProdutoDados || null);

  // Modais
  const [isFichaModalOpen, setIsFichaModalOpen] = useState(false);
  const [isIaModalOpen, setIsIaModalOpen] = useState(false);

  // Sincroniza se o pai atualizar produtoDados
  useEffect(() => {
    if (externalProdutoDados !== undefined) {
      setProdutoDadosRecord(externalProdutoDados);
    }
  }, [externalProdutoDados]);

  // Consulta produto_dados caso não tenha vindo do pai
  const loadProdutoDados = useCallback(async () => {
    if (!skuPai) return;
    try {
      const record = await produtoDadosService.getProdutoDados(skuPai);
      setProdutoDadosRecord(record);
      if (onProdutoDadosUpdated && record) {
        onProdutoDadosUpdated(record);
      }
    } catch (err) {
      setProdutoDadosRecord(null);
    }
  }, [skuPai, onProdutoDadosUpdated]);

  useEffect(() => {
    if (!externalProdutoDados) {
      loadProdutoDados();
    }
  }, [loadProdutoDados, externalProdutoDados]);

  // Consulta a tabela catalog para carregar a descrição rica do site
  const [catalogRecord, setCatalogRecord] = useState(null);

  const loadCatalogRecord = useCallback(async () => {
    if (!skuPai) return;
    try {
      const record = await pb.collection("catalog").getFirstListItem(`sku = "${skuPai}"`);
      setCatalogRecord(record);
    } catch (err) {
      setCatalogRecord(null);
    }
  }, [skuPai]);

  useEffect(() => {
    loadCatalogRecord();
  }, [loadCatalogRecord]);

  const fichaTecnica = produtoDadosRecord?.ficha_tecnica || null;
  const descriptionSmall = catalogRecord?.full_data?.description_small || item?.full_data?.description_small || "";

  return (
    <div className="space-y-6">
      {/* GRID DUPLO: FICHA TÉCNICA + ANÚNCIO IA */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* FICHA TÉCNICA EDITÁVEL */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-blue-600" />
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Ficha Técnica
              </h4>
            </div>
            <button
              onClick={() => setIsFichaModalOpen(true)}
              className="px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded text-[11px] font-bold border border-blue-200 flex items-center gap-1 transition-colors cursor-pointer"
            >
              <Edit2 size={12} />
              <span>Editar Ficha</span>
            </button>
          </div>

          {fichaTecnica ? (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                  <span className="text-[9px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Peso Bruto</span>
                  <span className="font-bold text-slate-800 font-mono">{fichaTecnica.peso_bruto_g || fichaTecnica.peso_gramas || "0"} g</span>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                  <span className="text-[9px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Liga / Metal</span>
                  <span className="font-bold text-slate-800">{fichaTecnica.liga_metal || "Prata 925"}</span>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                  <span className="text-[9px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Tempo Produção</span>
                  <span className="font-bold text-slate-800 font-mono">
                    {fichaTecnica.tempo_producao_dias ? `${fichaTecnica.tempo_producao_dias} dias` : `${Math.round((fichaTecnica.tempo_producao_minutos || 0) / 480) || 3} dias`}
                  </span>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                  <span className="text-[9px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Total de Pedras</span>
                  <span className="font-bold text-slate-800 font-mono">{fichaTecnica.qtd_pedras || 0}</span>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 col-span-2 sm:col-span-2">
                  <span className="text-[9px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Dimensões</span>
                  <span className="font-bold text-slate-800">{fichaTecnica.dimensoes || "Não informadas"}</span>
                </div>
              </div>
              {fichaTecnica.observacoes_tecnicas && (
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                  <span className="text-[9px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">Observações Técnicas</span>
                  <p className="text-[11px] text-slate-600 leading-relaxed">{fichaTecnica.observacoes_tecnicas}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="py-4 text-center text-slate-400 text-xs italic">
              Nenhuma ficha técnica cadastrada. Clique em "Editar Ficha" para preencher.
            </div>
          )}
        </div>

        {/* ANÚNCIO E GERADOR DE IA */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-2">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-purple-600" />
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Anúncio & IA Gerador
              </h4>
            </div>
            <button
              onClick={() => setIsIaModalOpen(true)}
              className="px-2.5 py-1 bg-purple-600 text-white hover:bg-purple-700 rounded text-[11px] font-bold shadow flex items-center gap-1 transition-all cursor-pointer"
            >
              <Sparkles size={12} />
              <span>Gerar Anúncio (IA)</span>
            </button>
          </div>

          <div className="bg-slate-50 p-3 rounded text-xs text-slate-600 max-h-32 overflow-y-auto space-y-1">
            <p className="font-semibold text-slate-800">Descrição Atual do Produto (Resumo):</p>
            <p className="text-[11px] leading-relaxed text-slate-500">
              {descriptionSmall ? descriptionSmall : "Sem resumo cadastrado na loja."}
            </p>
          </div>
        </div>

      </div>

      {/* MODAL FICHA TÉCNICA */}
      {isFichaModalOpen && (
        <FichaTecnicaModal
          isOpen={isFichaModalOpen}
          onClose={() => setIsFichaModalOpen(false)}
          skuPai={skuPai}
          initialFicha={produtoDadosRecord?.ficha_tecnica}
          onSuccess={loadProdutoDados}
        />
      )}

      {/* MODAL IA GERADOR DE ANÚNCIOS */}
      {isIaModalOpen && (
        <GeradorAnuncioIAModal
          isOpen={isIaModalOpen}
          onClose={() => setIsIaModalOpen(false)}
          skuPai={skuPai}
          item={item}
          onSuccess={async () => {
            await loadCatalogRecord();
            await loadProdutoDados();
          }}
        />
      )}
    </div>
  );
}
